import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContactForm from './components/ContactForm';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'AntiquesStore',
  name: 'Continental Antiques',
  url: SITE_URL,
  telephone: '+14389373161',
  email: 'dawoodhas334@gmail.com',
  image: `${SITE_URL}/og-image.jpg`,
  description:
    'Continental Antiques in Westmount, near downtown Montreal, specializes in European antique furniture, fine art, gold & silver buying, watch servicing, jewelry repair, rug cleaning, and prop rentals for films and events.',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '4909A Sherbrooke Ouest',
    addressLocality: 'Westmount',
    addressRegion: 'QC',
    postalCode: 'H3Z 1H2',
    addressCountry: 'CA',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 45.47312,
    longitude: -73.60178,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '10:00',
      closes: '18:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Saturday'],
      opens: '10:00',
      closes: '17:00',
    },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Antique Services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Antique Buying & Selling' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Antique Appraisal & Estimation' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Watch Servicing & Jewelry Repair' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Buy & Sell Gold & Silver' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Antique Prop Rentals for Films & Events' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Rug Cleaning' } },
    ],
  },
  sameAs: [
    'https://www.facebook.com/continentalantiques',
    'https://www.instagram.com/continentalantiques',
    'https://www.pinterest.com/continentalantiques',
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Where is Continental Antiques located?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Continental Antiques is located at 4909A Sherbrooke Ouest, Westmount, QC H3Z 1H2, Canada — near downtown Montreal.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does Continental Antiques buy gold and silver?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Continental Antiques buys and sells gold and silver items including jewelry, coins, and decorative objects.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does Continental Antiques offer antique appraisals?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. We provide professional antique appraisal and estimation services for furniture, fine art, jewelry, timepieces, and decorative arts.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does Continental Antiques offer prop rentals?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. We offer antique prop rentals for films, events, and photo shoots from our curated collection.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does Continental Antiques offer rug cleaning?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. We provide professional rug cleaning services for antique and fine rugs.',
      },
    },
  ],
};

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const currentLang = i18n.language === 'fr' ? 'fr' : 'en';

  const toggleLanguage = () => {
    i18n.changeLanguage(currentLang === 'en' ? 'fr' : 'en');
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const injectSchema = (id: string, data: object) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        (el as HTMLScriptElement).type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };
    injectSchema('schema-local-business', localBusinessSchema);
    injectSchema('schema-faq', faqSchema);
    return () => {
      ['schema-local-business', 'schema-faq'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img
                src="https://public.readdy.ai/ai/img_res/67aec7fb-d9f1-435f-a28f-5a0bc7dabff9.png"
                alt="Continental Antiques Logo"
                className="h-16 w-auto object-contain"
              />
            </div>

            <div className="hidden md:flex items-center space-x-8">
              {[
                { key: 'nav_home', id: 'home' },
                { key: 'nav_about', id: 'about' },
                { key: 'nav_services', id: 'services' },
                { key: 'nav_contact', id: 'contact' },
                { key: 'nav_estimation', id: 'contact-form' },
                { key: 'nav_looking_for', id: 'looking-for' },
              ].map(({ key, id }) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-800 hover:text-amber-700' : 'text-white hover:text-amber-300'}`}
                >
                  {t(key)}
                </button>
              ))}

              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className={`flex items-center gap-1.5 text-sm font-semibold border rounded-full px-3 py-1 transition-all whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'border-amber-700 text-amber-700 hover:bg-amber-700 hover:text-white'
                    : 'border-white text-white hover:bg-white hover:text-amber-800'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-translate-2 text-sm"></i>
                </div>
                {currentLang === 'en' ? 'FR' : 'EN'}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/0e559cac5ebc78ecb79dd049cc68ef05.jpg"
            alt="Continental Antiques Showroom"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 text-center text-white px-6 w-full">
          <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('hero_title')}
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('hero_subtitle')}
          </p>
          <button
            onClick={() => scrollToSection('estimation')}
            className="bg-amber-700 hover:bg-amber-800 text-white px-8 py-4 rounded-md text-base font-medium transition-all whitespace-nowrap cursor-pointer"
          >
            {t('hero_cta')}
          </button>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-amber-700 text-white px-8 py-3 mb-8">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>{t('about_title')}</h2>
            </div>
          </div>

          {/* Sub-section 1: About Continental Antiques */}
          <div className="mb-20">
            <h3 className="text-3xl font-semibold mb-8 text-gray-900 border-b border-amber-200 pb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t('about_section1_title')}
            </h3>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="w-full h-96">
                <img
                  src="https://static.readdy.ai/image/45564f3376387d63a1ae2c1d3a66eb75/cee3a99fcb00e688c8ab724f2a2cb3e8.png"
                  alt="Continental Antiques Owner"
                  className="w-full h-full object-cover object-top rounded-lg shadow-lg"
                />
              </div>
              <div>
                <p className="text-base text-gray-700 mb-6 leading-relaxed">{t('about_p1')}</p>
                <p className="text-base text-gray-700 leading-relaxed">{t('about_p3')}</p>
              </div>
            </div>
          </div>

          {/* Sub-sections 2 & 3: Our Story and Our Mission side by side */}
          <div className="grid md:grid-cols-2 gap-12">
            {/* Our Story */}
            <div>
              <h3 className="text-3xl font-semibold mb-8 text-gray-900 border-b border-amber-200 pb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                {t('about_section2_title')}
              </h3>
              <p className="text-base text-gray-700 leading-relaxed">{t('about_heritage_p1')}</p>
            </div>

            {/* Our Mission */}
            <div>
              <h3 className="text-3xl font-semibold mb-8 text-gray-900 border-b border-amber-200 pb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                {t('about_section3_title')}
              </h3>
              <p className="text-base text-gray-700 leading-relaxed">{t('about_mission_p1')}</p>
            </div>
          </div>

        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-amber-700 text-white px-8 py-3 mb-8">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>{t('services_title')}</h2>
            </div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{t('services_subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: 'ri-shopping-bag-3-line', titleKey: 'service1_title', descKey: 'service1_desc' },
              { icon: 'ri-search-eye-line', titleKey: 'service2_title', descKey: 'service2_desc' },
              { icon: 'ri-tools-line', titleKey: 'service3_title', descKey: 'service3_desc' },
              { icon: 'ri-compass-3-line', titleKey: 'service4_title', descKey: 'service4_desc' },
              { icon: 'ri-home-smile-line', titleKey: 'service5_title', descKey: 'service5_desc' },
              { icon: 'ri-truck-line', titleKey: 'service6_title', descKey: 'service6_desc' },
            ].map(({ icon, titleKey, descKey }) => (
              <div key={titleKey} className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-amber-700/10 rounded-full flex items-center justify-center mb-6">
                  <i className={`${icon} text-3xl text-amber-700`}></i>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>{t(titleKey)}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{t(descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-amber-700 text-white px-8 py-3 mb-8">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>{t('contact_title')}</h2>
            </div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{t('contact_subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-semibold mb-6 text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>{t('contact_address_title')}</h3>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-amber-700/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-amber-700"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-gray-900 mb-1">{t('contact_address_label')}</h4>
                    <p className="text-sm text-gray-600">Continental Antiques</p>
                    <p className="text-sm text-gray-600">4909 A Sherbrooke Ouest</p>
                    <p className="text-sm text-gray-600">Westmount, QC H3Z 1H2</p>
                    <p className="text-sm text-gray-600">Canada</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-amber-700/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-amber-700"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-gray-900 mb-1">{t('contact_phone_label')}</h4>
                    <a href="tel:+14389373161" className="text-sm text-amber-700 hover:text-amber-800 cursor-pointer">
                      +1 (438) 937-3161
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-amber-700/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-amber-700"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-gray-900 mb-1">{t('contact_email_label')}</h4>
                    <a href="mailto:dawoodhas334@gmail.com" className="text-sm text-amber-700 hover:text-amber-800 cursor-pointer">
                      dawoodhas334@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-amber-700/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-xl text-amber-700"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-gray-900 mb-1">{t('contact_hours_label')}</h4>
                    <p className="text-sm text-gray-600">{t('contact_hours_1')}</p>
                    <p className="text-sm text-gray-600">{t('contact_hours_2')}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <h4 className="font-semibold text-gray-900 mb-4">{t('contact_follow')}</h4>
                <div className="flex space-x-4">
                  <a href="#" className="w-10 h-10 bg-amber-700 hover:bg-amber-800 rounded-full flex items-center justify-center text-white transition-colors cursor-pointer">
                    <i className="ri-facebook-fill text-lg"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-amber-700 hover:bg-amber-800 rounded-full flex items-center justify-center text-white transition-colors cursor-pointer">
                    <i className="ri-instagram-line text-lg"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-amber-700 hover:bg-amber-800 rounded-full flex items-center justify-center text-white transition-colors cursor-pointer">
                    <i className="ri-pinterest-fill text-lg"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="w-full h-96 md:h-full min-h-96 flex flex-col gap-3">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2796.4823!2d-73.60178!3d45.47312!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc91a4f5b5b5b5b%3A0x0!2s4909+Sherbrooke+St+W%2C+Westmount%2C+QC+H3Z+1H2%2C+Canada!5e0!3m2!1sen!2sca!4v1700000000000!5m2!1sen!2sca"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '400px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-lg shadow-md flex-1"
                title="Continental Antiques Location - 4909 Sherbrooke St W, Westmount"
              ></iframe>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=4909+Sherbrooke+St+W,+Westmount,+QC+H3Z+1H2,+Canada"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-6 py-3 rounded-md text-sm font-semibold transition-all whitespace-nowrap cursor-pointer w-full"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-navigation-line text-base"></i>
                </div>
                {t('contact_get_directions')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Estimation Section */}
      <section id="estimation" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-amber-700 text-white px-8 py-3 mb-8">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>{t('estimation_title')}</h2>
            </div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{t('estimation_subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="w-full h-96">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20antique%20appraiser%20examining%20vintage%20furniture%20with%20magnifying%20glass%20detailed%20inspection%20expert%20evaluation%20wooden%20desk%20classic%20interior%20warm%20lighting%20focused%20concentration%20authentication%20process&width=800&height=600&seq=appraisal001&orientation=landscape"
                alt="Antique Appraisal Service"
                className="w-full h-full object-cover object-top rounded-lg shadow-lg"
              />
            </div>

            <div>
              <h3 className="text-2xl font-semibold mb-6 text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>{t('estimation_how_title')}</h3>

              <div className="space-y-6">
                {[
                  { num: 1, titleKey: 'estimation_step1_title', descKey: 'estimation_step1_desc' },
                  { num: 2, titleKey: 'estimation_step2_title', descKey: 'estimation_step2_desc' },
                  { num: 3, titleKey: 'estimation_step3_title', descKey: 'estimation_step3_desc' },
                  { num: 4, titleKey: 'estimation_step4_title', descKey: 'estimation_step4_desc' },
                ].map(({ num, titleKey, descKey }) => (
                  <div key={num} className="flex items-start">
                    <div className="w-10 h-10 bg-amber-700 text-white rounded-full flex items-center justify-center flex-shrink-0 font-semibold">
                      {num}
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900 mb-2">{t(titleKey)}</h4>
                      <p className="text-sm text-gray-600">{t(descKey)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> {t('estimation_note')}
                </p>
              </div>

              <div className="mt-6">
                <button
                  onClick={() => scrollToSection('contact-form')}
                  className="bg-amber-700 hover:bg-amber-800 text-white px-8 py-3 rounded-md text-base font-medium transition-all whitespace-nowrap cursor-pointer"
                >
                  {t('hero_cta')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-amber-700 text-white px-8 py-3 mb-8">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>{t('form_title')}</h2>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('form_subtitle')}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-10">
            <ContactForm />
          </div>
        </div>
      </section>

      {/* What We're Looking For Section */}
      <section id="looking-for" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-amber-700 text-white px-8 py-3 mb-8">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>{t('looking_title')}</h2>
            </div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{t('looking_subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { img: 'https://readdy.ai/api/search-image?query=antique%20French%20oak%20armoire%20cabinet%20with%20ornate%20carved%20details%20brass%20hardware%20aged%20patina%20elegant%20period%20furniture%20displayed%20in%20warm%20studio%20lighting%20against%20neutral%20background%20no%20people&width=600&height=480&seq=looking001&orientation=landscape', alt: 'Antique Furniture', titleKey: 'looking_furniture_title', descKey: 'looking_furniture_desc' },
              { img: 'https://readdy.ai/api/search-image?query=classical%20oil%20painting%20in%20gilded%20frame%20landscape%20scene%20European%20master%20artwork%20canvas%20texture%20rich%20colors%20antique%20gold%20frame%20ornate%20details%20displayed%20on%20neutral%20wall%20no%20people%20no%20faces&width=600&height=480&seq=looking002&orientation=landscape', alt: 'Fine Art', titleKey: 'looking_fineart_title', descKey: 'looking_fineart_desc' },
              { img: 'https://readdy.ai/api/search-image?query=antique%20Meissen%20porcelain%20vase%20with%20hand%20painted%20floral%20motifs%20fine%20china%20decorative%20arts%20silver%20candlesticks%20crystal%20glassware%20arranged%20on%20velvet%20cloth%20warm%20studio%20lighting%20no%20people&width=600&height=480&seq=looking003&orientation=landscape', alt: 'Decorative Arts', titleKey: 'looking_decorative_title', descKey: 'looking_decorative_desc' },
              { img: 'https://readdy.ai/api/search-image?query=antique%20grandfather%20clock%20ornate%20wooden%20case%20brass%20pendulum%20vintage%20pocket%20watches%20laid%20on%20velvet%20cushion%20intricate%20mechanisms%20timepieces%20collection%20warm%20amber%20lighting%20no%20people%20no%20faces&width=600&height=480&seq=looking004&orientation=landscape', alt: 'Timepieces', titleKey: 'looking_timepieces_title', descKey: 'looking_timepieces_desc' },
              { img: 'https://readdy.ai/api/search-image?query=antique%20estate%20jewelry%20collection%20Art%20Nouveau%20brooch%20diamond%20ring%20pearl%20necklace%20emerald%20bracelet%20displayed%20on%20dark%20velvet%20tray%20elegant%20gemstones%20precious%20metals%20close%20up%20no%20people%20no%20faces&width=600&height=480&seq=looking005&orientation=landscape', alt: 'Jewelry', titleKey: 'looking_jewelry_title', descKey: 'looking_jewelry_desc' },
              { img: 'https://readdy.ai/api/search-image?query=Chinese%20antique%20porcelain%20vase%20blue%20white%20Ming%20dynasty%20Japanese%20bronze%20incense%20burner%20jade%20figurine%20Asian%20decorative%20objects%20arranged%20on%20wooden%20surface%20museum%20quality%20no%20people%20no%20faces&width=600&height=480&seq=looking006&orientation=landscape', alt: 'Asian Arts', titleKey: 'looking_asian_title', descKey: 'looking_asian_desc' },
              { img: 'https://readdy.ai/api/search-image?query=Galle%20cameo%20art%20glass%20vase%20with%20floral%20etching%20Daum%20Nancy%20lamp%20Lalique%20crystal%20bowl%20art%20nouveau%20glass%20objects%20displayed%20on%20shelf%20warm%20backlighting%20glowing%20translucent%20no%20people%20no%20faces&width=600&height=480&seq=looking007&orientation=landscape', alt: 'Art Glass', titleKey: 'looking_glass_title', descKey: 'looking_glass_desc' },
              { img: 'https://readdy.ai/api/search-image?query=mid%20century%20modern%20vintage%20collectibles%20retro%20ceramic%20vase%20Bakelite%20radio%20vintage%20camera%20collection%201950s%20objects%20arranged%20on%20wooden%20shelf%20warm%20studio%20lighting%20no%20people%20no%20faces&width=600&height=480&seq=looking008&orientation=landscape', alt: 'Vintage Collectibles', titleKey: 'looking_vintage_title', descKey: 'looking_vintage_desc' },
              { img: 'https://readdy.ai/api/search-image?query=antique%20architectural%20salvage%20ornate%20cast%20iron%20fireplace%20mantel%20carved%20stone%20column%20fragment%20vintage%20wrought%20iron%20gate%20decorative%20elements%20aged%20patina%20warm%20natural%20light%20no%20people%20no%20faces&width=600&height=480&seq=looking009&orientation=landscape', alt: 'Architectural Elements', titleKey: 'looking_architectural_title', descKey: 'looking_architectural_desc' },
            ].map(({ img, alt, titleKey, descKey }) => (
              <div key={titleKey} className="group cursor-pointer">
                <div className="w-full h-64 mb-4 overflow-hidden rounded-lg shadow-md">
                  <img src={img} alt={alt} className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-300" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>{t(titleKey)}</h3>
                <p className="text-sm text-gray-600">{t(descKey)}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-base text-gray-700 mb-6">{t('looking_cta_text')}</p>
            <button
              onClick={() => scrollToSection('contact-form')}
              className="bg-amber-700 hover:bg-amber-800 text-white px-8 py-3 rounded-md text-base font-medium transition-all whitespace-nowrap cursor-pointer"
            >
              {t('looking_cta_btn')}
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-amber-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Continental Antiques</h4>
              <p className="text-sm text-amber-100">{t('footer_tagline')}</p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t('footer_quick_links')}</h4>
              <ul className="space-y-2">
                {[
                  { key: 'nav_home', id: 'home' },
                  { key: 'nav_about', id: 'about' },
                  { key: 'nav_services', id: 'services' },
                  { key: 'nav_contact', id: 'contact' },
                ].map(({ key, id }) => (
                  <li key={id}>
                    <button onClick={() => scrollToSection(id)} className="text-sm text-amber-100 hover:text-white transition-colors cursor-pointer">
                      {t(key)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t('footer_contact_info')}</h4>
              <ul className="space-y-2 text-sm text-amber-100">
                <li>4909 A Sherbrooke Ouest</li>
                <li>Westmount, QC H3Z 1H2</li>
                <li>Canada</li>
                <li className="pt-2">
                  <a href="tel:+14389373161" className="hover:text-white transition-colors cursor-pointer">+1 (438) 937-3161</a>
                </li>
                <li>
                  <a href="mailto:dawoodhas334@gmail.com" className="hover:text-white transition-colors cursor-pointer">dawoodhas334@gmail.com</a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t('footer_hours')}</h4>
              <ul className="space-y-2 text-sm text-amber-100">
                <li>{t('footer_hours_1')}</li>
                <li>{t('footer_hours_2')}</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-amber-700 pt-8 text-center">
            <p className="text-sm text-amber-100">{t('footer_copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
